/**
 * Composição da carteira por categoria DESCONSIDERANDO a parte alocada em boxes.
 *
 * Cada ativo entra apenas pelo valor livre (fora de boxes), usando o
 * `box_applied_percentage` (fonte de verdade) sobre o bruto atualizado:
 *   livre = (updateValue + total_investment_box_applied_value) × (1 − pct)
 *
 * Centraliza a regra usada nos gráficos de composição/distribuição da carteira
 * (CompositionGraphicWallet e PortfolioBalanceSheetStatistics).
 */

const STOCK_TYPES = ['stock', 'stocks'];
const ETF_TYPES = ['etf', 'etfs'];
const REIT_TYPES = ['reit', 'reits'];
const CRYPTO_TYPES = ['crypto', 'cripto'];

export interface BoxAdjustedComposition {
  fii: number;
  action: number;
  rendaFixa: number;
  stocks: number;
  etfs: number;
  reits: number;
  crypto: number;
}

/** Valor do ativo fora de boxes (bruto atualizado × (1 − box_applied_percentage)). */
export function freeValueOutOfBox(it: any): number {
  const gross =
    Number(it?.updateValue || 0) +
    Number(it?.total_investment_box_applied_value || 0);
  const pct = Math.min(Math.max(Number(it?.box_applied_percentage || 0), 0), 1);
  return gross * (1 - pct);
}

function categoryOf(productTypeName: string): keyof BoxAdjustedComposition {
  const type = (productTypeName || '').toLowerCase();
  if (type === 'acao') return 'action';
  if (type === 'fii') return 'fii';
  if (STOCK_TYPES.includes(type)) return 'stocks';
  if (ETF_TYPES.includes(type)) return 'etfs';
  if (REIT_TYPES.includes(type)) return 'reits';
  if (CRYPTO_TYPES.includes(type)) return 'crypto';
  return 'rendaFixa';
}

/** Percentuais (0–100, 2 casas) por categoria, já líquidos da parte em boxes. */
export function buildBoxAdjustedComposition(
  positionsAll: any[]
): BoxAdjustedComposition {
  const sums: BoxAdjustedComposition = {
    fii: 0,
    action: 0,
    rendaFixa: 0,
    stocks: 0,
    etfs: 0,
    reits: 0,
    crypto: 0,
  };

  (positionsAll ?? [])
    .filter((it: any) => !it?.reservadeemergencia && !it?.alocado_total)
    .forEach((it: any) => {
      sums[categoryOf(it?.productTypeName)] += freeValueOutOfBox(it);
    });

  const total =
    sums.fii +
    sums.action +
    sums.rendaFixa +
    sums.stocks +
    sums.etfs +
    sums.reits +
    sums.crypto;

  const pct = (value: number) =>
    total === 0 ? 0 : Number(((value / total) * 100).toFixed(2));

  return {
    fii: pct(sums.fii),
    action: pct(sums.action),
    rendaFixa: pct(sums.rendaFixa),
    stocks: pct(sums.stocks),
    etfs: pct(sums.etfs),
    reits: pct(sums.reits),
    crypto: pct(sums.crypto),
  };
}
